import { BusinessObjectBase } from './base';
import { processMessagePropertySchema } from '@schemas/business-objects/process-message-property';
import type { ProcessDataType } from './process-data-type';
import type { ProcessMessage } from './process-message';
import type { Id } from '../base-types';

export interface ProcessMessagePropertyData {
  readonly id: Id;
  readonly name?: string;
  readonly isArray?: boolean;
  readonly required?: boolean;
  readonly description?: string;
  readonly sequence?: number;
  readonly messageId: Id;
  readonly message?: ProcessMessage;
  readonly typeId: Id;
  readonly type?: ProcessDataType;
}

export interface ProcessMessageProperty extends Readonly<ProcessMessagePropertyData> {}
type ProcessMessagePropertyCreateWithChildrenData =
  Partial<ProcessMessagePropertyData>;

export class ProcessMessageProperty extends BusinessObjectBase {
  static readonly entityName = 'ProcessMessageProperty' as const;
  static readonly plural = 'process-message-properties';
  static schema = processMessagePropertySchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ProcessMessageProperty[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ProcessMessageProperty(data));
  }

  static async findOne(filter?: any): Promise<ProcessMessageProperty | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ProcessMessageProperty(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ProcessMessageProperty> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ProcessMessageProperty(data);
  }

  static async create(
    data: Omit<ProcessMessagePropertyData, 'id'>,
  ): Promise<ProcessMessageProperty> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ProcessMessageProperty(result);
  }

  static async createMany(
    data: Omit<ProcessMessagePropertyData, 'id'>[],
  ): Promise<ProcessMessageProperty[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessMessageProperty(item));
  }

  static async createWithChildren(
    data: ProcessMessagePropertyCreateWithChildrenData,
  ): Promise<ProcessMessageProperty> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ProcessMessageProperty(result);
  }

  static async createManyWithChildren(
    data: ProcessMessagePropertyCreateWithChildrenData[],
  ): Promise<ProcessMessageProperty[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessMessageProperty(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ProcessMessagePropertyData>,
  ): Promise<ProcessMessageProperty> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ProcessMessageProperty(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
